import { findActionLocations } from '@/utils/workflow';

/**
 * JSON Path utilities for reading/writing values at arbitrary dot-separated paths
 * within a workflow definition. This allows node-type-agnostic operations —
 * the same functions work for actions, triggers, tools, nested scoped actions, etc.
 * 
 * Paths use dot notation, e.g.:
 *   "definition.triggers.manual"
 *   "definition.actions.Validate_File"
 *   "definition.actions.My_If.actions.Send_email"
 *   "definition.actions.Agent_Loop.tools.myTool"
 */

/**
 * Get a value at a dot-separated path within an object.
 * Returns undefined if any segment along the path doesn't exist.
 */
export function getValueAtPath(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Set a value at a dot-separated path within an object (mutates in place).
 * Creates intermediate objects if they don't exist.
 * Returns true if set succeeded.
 */
export function setValueAtPath(obj: unknown, path: string, value: unknown): boolean {
  const parts = path.split('.');
  if (parts.length === 0) return false;

  let current: Record<string, unknown> = obj as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    if (current[part] == null || typeof current[part] !== 'object') {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }

  const finalKey = parts[parts.length - 1]!;
  current[finalKey] = value;
  return true;
}

/**
 * Delete a value at a dot-separated path within an object (mutates in place).
 * Returns true if the value was found and deleted.
 */
export function deleteValueAtPath(obj: unknown, path: string): boolean {
  const parts = path.split('.');
  if (parts.length === 0) return false;

  let current: Record<string, unknown> = obj as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    if (current[part] == null || typeof current[part] !== 'object') return false;
    current = current[part] as Record<string, unknown>;
  }

  const finalKey = parts[parts.length - 1]!;
  if (finalKey in current) {
    delete current[finalKey];
    return true;
  }
  return false;
}

/**
 * Resolve the JSON path for a node within a workflow definition.
 * Searches recursively through actions (including If/Foreach/Switch/Scope/Until/Agent containers)
 * and triggers. Returns the full dot-path from the workflow root.
 * 
 * @param workflow - The full workflow definition object
 * @param nodeName - The name of the node to find
 * @param nodeType - Optional hint: 'trigger', 'action', or 'tool'
 * @param parentAgentName - For tools, the name of the parent Agent action
 * @returns The dot-path e.g. "definition.actions.My_If.else.actions.Send_email", or null if not found
 */
export function resolveNodePath(
  workflow: Record<string, unknown>,
  nodeName: string,
  nodeType?: 'trigger' | 'action' | 'tool',
  parentAgentName?: string,
): string | null {
  const definition = workflow.definition as Record<string, unknown> | undefined;
  if (!definition) return null;

  const actions = definition.actions as Record<string, Record<string, unknown>> | undefined;

  // For tools, locate the parent agent recursively before checking its tools.
  if (nodeType === 'tool' && parentAgentName) {
    const agentName = parentAgentName.includes('/') ? parentAgentName.split('/').pop()! : parentAgentName;
    const agentLocations = findActionLocations(actions, agentName, 'definition.actions');
    if (agentLocations.length !== 1) return null;

    const agentLocation = agentLocations[0]!;
    const tools = agentLocation.action.tools;
    if (tools && typeof tools === 'object' && Object.hasOwn(tools, nodeName)) {
      return `${agentLocation.actionPath}.tools.${nodeName}`;
    }
    return null;
  }

  // Check triggers (flat, no nesting)
  if (nodeType !== 'action') {
    const triggers = definition.triggers as Record<string, unknown> | undefined;
    if (triggers && Object.hasOwn(triggers, nodeName)) {
      return `definition.triggers.${nodeName}`;
    }
    if (nodeType === 'trigger') return null;
  }

  // Search actions recursively
  const actionLocation = findActionLocations(actions, nodeName, 'definition.actions')[0];
  return actionLocation?.actionPath ?? null;
}
